import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { LockCard, MediaGroup, MonthScrubber } from '@/components/klient/knihovna'
import { EmptyState } from '@/components/shared'
import { ACTIVE_FILTER, FILTERS, SHOOT_GROUPS } from '@/mocks/klient/knihovna'

/**
 * Two states the library can be in before it is simply itself: the first-month
 * lock (spec §8 — photos exist, downloads wait) and the client's first week,
 * where nothing has been shot yet. One state at a time.
 */
type KnihovnaSearch = { stav?: 'zamceno' | 'prvni-tyden' }

export const Route = createFileRoute('/klient/knihovna')({
  validateSearch: (search: Record<string, unknown>): KnihovnaSearch =>
    search.stav === 'zamceno' || search.stav === 'prvni-tyden' ? { stav: search.stav } : {},
  head: () => ({ meta: [{ title: 'Knihovna · Aplikace Bitez' }] }),
  component: KlientKnihovna,
})

const monthAnchor = (index: number) => `mesic-${index}`

function KlientKnihovna() {
  const { stav } = Route.useSearch()
  const locked = stav === 'zamceno'
  const empty = stav === 'prvni-tyden'

  /* Before the first shoot there is nothing to filter, scrub or unlock — the
     screen is one promise instead of a wall of empty chrome. */
  if (empty) {
    return (
      <div className="flex min-h-full flex-col px-4 pt-6 pb-6">
        <EmptyState
          sticker="folder"
          title="Zatím tu nic není"
          note="Po prvním focení tady najdete všechny fotky a videa — ke stažení, kdykoliv a napořád."
          action={{ label: 'Domluvit focení', href: '/klient/foceni?stav=prvni' }}
        />
      </div>
    )
  }

  return (
    <div className="px-4 pt-6 pb-6">
      {/* Frameless stat-filters: fill icon + brand + tilted underline mark
          the active one — no pill chrome */}
      {!locked && (
        <div className="animate-rise-up flex gap-5">
          {FILTERS.map((filter) => {
            const active = filter.label === ACTIVE_FILTER
            return (
              <button
                key={filter.label}
                type="button"
                className="group flex cursor-pointer flex-col gap-1"
              >
                <span
                  className={`flex items-center gap-1.5 text-sm font-bold transition-colors duration-150 ${
                    active ? 'text-brand' : 'text-ink/40 group-hover:text-ink/70'
                  }`}
                >
                  <JellyIcon
                    name={filter.icon}
                    variant={active ? 'fill' : 'line'}
                    className="size-4"
                  />
                  {filter.label}
                  <span className="text-[10px] font-semibold opacity-70">{filter.count}</span>
                </span>
                <span
                  aria-hidden
                  className={`h-1 w-7 -rotate-2 rounded-full ${active ? 'bg-brand' : 'bg-transparent'}`}
                />
              </button>
            )
          })}
        </div>
      )}

      {locked && (
        <div className="animate-rise-up">
          <LockCard unlockDate="9. května" countdown="21 dní" />
        </div>
      )}

      {/* Drag-to-scrub on the right edge (Google Photos): hidden until you
          scroll, snaps to a month on release. */}
      <MonthScrubber
        months={SHOOT_GROUPS.map((group, index) => ({
          label: group.month,
          id: monthAnchor(index),
        }))}
      />

      <div className="mt-3 space-y-8">
        {SHOOT_GROUPS.map((group, index) => (
          <div
            key={group.month}
            className="animate-rise-up"
            style={{ animationDelay: `${(index + 1) * 90}ms` }}
          >
            <MediaGroup group={group} id={monthAnchor(index)} locked={locked} />
          </div>
        ))}
      </div>
    </div>
  )
}
